/**
 * Phase H5 FE-1 — TanStack Query hooks for the F7 / F8 assessment API.
 *
 * Wraps the BE endpoints mounted at `/assessments`:
 *   * Topics CRUD (POST / GET / PATCH / DELETE `/topics`)
 *   * Questions CRUD + approve / archive (`/questions`)
 *   * Templates CRUD + activate / archive (`/templates`)
 *   * Per-blueprint-item bank availability (`/templates/{slug}/availability`)
 *
 * Read keys are coarse — the IA page filters in-memory after a single
 * `limit=200` round trip in most cases. Cohorts that legitimately exceed
 * 200 questions or 50 templates can opt into server-side pagination by
 * passing explicit `limit` / `skip` in the filters arg; the query key
 * carries them so the cache stays correct.
 *
 * Mutations invalidate the relevant read root (`["assessment","topics"]`,
 * `["assessment","questions"]`, `["assessment","templates"]`) so the
 * DataTable pages reflect writes without an explicit refetch.
 *
 * Iron Rule #1 — every endpoint here is BE-verified (see PR #N of the
 * F7 / F8 phases). Iron Rule #2 — no `Record<string, any>` shrugs; the
 * shared types catalog the real payload shapes 1:1.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AssessmentBlueprintAvailabilityResponse,
  AssessmentGenerateQuestionsRequest,
  AssessmentGenerateQuestionsResponse,
  AssessmentQuestionListFilters,
  AssessmentQuestionListResponse,
  AssessmentQuestionPublic,
  AssessmentRegenerateQuestionRequest,
  AssessmentRegenerateQuestionResponse,
  AssessmentTemplateCreate,
  AssessmentTemplateListFilters,
  AssessmentTemplateListResponse,
  AssessmentTemplatePublic,
  AssessmentTemplateUpdate,
  AssessmentTopicCreate,
  AssessmentTopicListResponse,
  AssessmentTopicPublic,
  AssessmentTopicUpdate,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

const TOPICS_KEY_ROOT = ["assessment", "topics"] as const;
const QUESTIONS_KEY_ROOT = ["assessment", "questions"] as const;
const TEMPLATES_KEY_ROOT = ["assessment", "templates"] as const;
const TEMPLATE_AVAILABILITY_KEY_ROOT = ["assessment", "template-availability"] as const;

// ── Topics ──────────────────────────────────────────────────────────

export interface UseAssessmentTopicsOptions {
  subject?: string;
  parentSlug?: string;
  search?: string;
  includeArchived?: boolean;
  enabled?: boolean;
}

export function useAssessmentTopics(opts: UseAssessmentTopicsOptions = {}) {
  const {
    subject = null,
    parentSlug = null,
    search = null,
    includeArchived = false,
    enabled = true,
  } = opts;
  return useQuery<AssessmentTopicListResponse>({
    queryKey: [...TOPICS_KEY_ROOT, { subject, parentSlug, search, includeArchived }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (subject) params.set("subject", subject);
      if (parentSlug) params.set("parent_slug", parentSlug);
      if (search) params.set("search", search);
      if (includeArchived) params.set("include_archived", "true");
      const query = params.toString();
      const url = query ? `/assessments/topics?${query}` : "/assessments/topics";
      return api.get<AssessmentTopicListResponse>(url);
    },
    enabled,
    staleTime: 60_000,
  });
}

function invalidateTopics(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: TOPICS_KEY_ROOT });
}

export function useCreateAssessmentTopic() {
  const qc = useQueryClient();
  return useMutation<AssessmentTopicPublic, Error, AssessmentTopicCreate>({
    mutationKey: [...TOPICS_KEY_ROOT, "create"],
    mutationFn: (body) => api.post<AssessmentTopicPublic>("/assessments/topics", body),
    onSuccess: () => invalidateTopics(qc),
  });
}

export interface UseUpdateAssessmentTopicVars {
  slug: string;
  body: AssessmentTopicUpdate;
}

export function useUpdateAssessmentTopic() {
  const qc = useQueryClient();
  return useMutation<AssessmentTopicPublic, Error, UseUpdateAssessmentTopicVars>({
    mutationKey: [...TOPICS_KEY_ROOT, "update"],
    mutationFn: ({ slug, body }) =>
      api.patch<AssessmentTopicPublic>(`/assessments/topics/${slug}`, body),
    onSuccess: () => invalidateTopics(qc),
  });
}

export interface UseArchiveAssessmentTopicVars {
  slug: string;
  reason?: string;
}

export function useArchiveAssessmentTopic() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, Error, UseArchiveAssessmentTopicVars>({
    mutationKey: [...TOPICS_KEY_ROOT, "archive"],
    mutationFn: ({ slug, reason }) => {
      const search = new URLSearchParams();
      if (reason) search.set("reason", reason);
      const query = search.toString();
      const url = query ? `/assessments/topics/${slug}?${query}` : `/assessments/topics/${slug}`;
      return api.delete<{ ok: boolean }>(url);
    },
    onSuccess: () => invalidateTopics(qc),
  });
}

// ── Questions ───────────────────────────────────────────────────────

export function useAssessmentQuestions(filters: AssessmentQuestionListFilters = {}) {
  return useQuery<AssessmentQuestionListResponse>({
    queryKey: [...QUESTIONS_KEY_ROOT, "list", filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.topic_slug) params.set("topic_slug", filters.topic_slug);
      if (filters.type) params.set("type", filters.type);
      if (filters.difficulty) params.set("difficulty", filters.difficulty);
      if (filters.language) params.set("language", filters.language);
      if (filters.grade !== undefined) params.set("grade", String(filters.grade));
      if (filters.status) params.set("status", filters.status);
      if (filters.origin) params.set("origin", filters.origin);
      if (filters.include_archived) params.set("include_archived", "true");
      params.set("limit", String(filters.limit ?? 100));
      if (filters.skip) params.set("skip", String(filters.skip));
      const url = `/assessments/questions?${params.toString()}`;
      return api.get<AssessmentQuestionListResponse>(url);
    },
    staleTime: 30_000,
  });
}

export function useAssessmentQuestion(id: string | null | undefined) {
  return useQuery<AssessmentQuestionPublic>({
    queryKey: [...QUESTIONS_KEY_ROOT, "detail", id],
    queryFn: () => api.get<AssessmentQuestionPublic>(`/assessments/questions/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

function invalidateQuestions(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: QUESTIONS_KEY_ROOT });
}

export interface UseApproveAssessmentQuestionVars {
  id: string;
  approvalNote?: string;
}

export function useApproveAssessmentQuestion() {
  const qc = useQueryClient();
  return useMutation<AssessmentQuestionPublic, Error, UseApproveAssessmentQuestionVars>({
    mutationKey: [...QUESTIONS_KEY_ROOT, "approve"],
    mutationFn: ({ id, approvalNote }) =>
      api.post<AssessmentQuestionPublic>(`/assessments/questions/${id}:approve`, {
        approval_note: approvalNote ?? null,
      }),
    onSuccess: () => invalidateQuestions(qc),
  });
}

export interface UseArchiveAssessmentQuestionVars {
  id: string;
  reason: string;
}

export function useArchiveAssessmentQuestion() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, Error, UseArchiveAssessmentQuestionVars>({
    mutationKey: [...QUESTIONS_KEY_ROOT, "archive"],
    // BE accepts the reason via a JSON body on DELETE — axios passes it via
    // `config.data` (RFC 9110 permits a body on DELETE; the FastAPI route
    // declares it explicitly).
    mutationFn: ({ id, reason }) =>
      api.delete<{ ok: boolean }>(`/assessments/questions/${id}`, {
        data: { reason },
      }),
    onSuccess: () => invalidateQuestions(qc),
  });
}

// ── AI generation (Phase F11) ────────────────────────────────────────

export interface UseGenerateQuestionsBatchVars {
  topicSlug: string;
  body: AssessmentGenerateQuestionsRequest;
}

/**
 * `POST /assessments/topics/{topicSlug}/generate-questions:batch`
 *
 * Calls the F9 AI question chain (Gemini primary → NVIDIA NIM fallback)
 * and persists every accepted draft as a fresh question with
 * `status="draft"` and `origin="ai"`. The mutation invalidates the
 * Question Bank list cache on success so the new drafts appear in the
 * page DataTable without an explicit refetch.
 */
export function useGenerateAssessmentQuestionsBatch() {
  const qc = useQueryClient();
  return useMutation<AssessmentGenerateQuestionsResponse, Error, UseGenerateQuestionsBatchVars>({
    mutationKey: [...QUESTIONS_KEY_ROOT, "ai-generate"],
    mutationFn: ({ topicSlug, body }) =>
      api.post<AssessmentGenerateQuestionsResponse>(
        `/assessments/topics/${encodeURIComponent(topicSlug)}/generate-questions:batch`,
        body,
      ),
    onSuccess: () => invalidateQuestions(qc),
  });
}

export interface UseRegenerateAssessmentQuestionVars {
  id: string;
  body?: AssessmentRegenerateQuestionRequest;
}

/**
 * `POST /assessments/questions/{id}:regenerate`
 *
 * Replaces an AI-origin draft's content in place with a fresh
 * generation (document id preserved; `ai_call_log_id` + `ai_model_id`
 * refreshed). 422 if the question is not `status=draft` / not
 * `origin=ai`.
 */
export function useRegenerateAssessmentQuestion() {
  const qc = useQueryClient();
  return useMutation<
    AssessmentRegenerateQuestionResponse,
    Error,
    UseRegenerateAssessmentQuestionVars
  >({
    mutationKey: [...QUESTIONS_KEY_ROOT, "ai-regenerate"],
    mutationFn: ({ id, body }) =>
      api.post<AssessmentRegenerateQuestionResponse>(
        `/assessments/questions/${encodeURIComponent(id)}:regenerate`,
        body ?? {},
      ),
    onSuccess: () => invalidateQuestions(qc),
  });
}

// ── Templates ───────────────────────────────────────────────────────

export function useAssessmentTemplates(filters: AssessmentTemplateListFilters = {}) {
  return useQuery<AssessmentTemplateListResponse>({
    queryKey: [...TEMPLATES_KEY_ROOT, "list", filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.grade !== undefined) params.set("grade", String(filters.grade));
      if (filters.language) params.set("language", filters.language);
      if (filters.include_archived) params.set("include_archived", "true");
      if (filters.search) params.set("search", filters.search);
      params.set("limit", String(filters.limit ?? 100));
      if (filters.skip) params.set("skip", String(filters.skip));
      const url = `/assessments/templates?${params.toString()}`;
      return api.get<AssessmentTemplateListResponse>(url);
    },
    staleTime: 30_000,
  });
}

export function useAssessmentTemplate(slug: string | null | undefined) {
  return useQuery<AssessmentTemplatePublic>({
    queryKey: [...TEMPLATES_KEY_ROOT, "detail", slug],
    queryFn: () => api.get<AssessmentTemplatePublic>(`/assessments/templates/${slug}`),
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

export function useAssessmentTemplateAvailability(slug: string | null | undefined) {
  return useQuery<AssessmentBlueprintAvailabilityResponse>({
    queryKey: [...TEMPLATE_AVAILABILITY_KEY_ROOT, slug],
    queryFn: () =>
      api.get<AssessmentBlueprintAvailabilityResponse>(
        `/assessments/templates/${slug}/availability`,
      ),
    enabled: Boolean(slug),
    staleTime: 15_000,
  });
}

function invalidateTemplates(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: TEMPLATES_KEY_ROOT });
  void qc.invalidateQueries({ queryKey: TEMPLATE_AVAILABILITY_KEY_ROOT });
}

export function useCreateAssessmentTemplate() {
  const qc = useQueryClient();
  return useMutation<AssessmentTemplatePublic, Error, AssessmentTemplateCreate>({
    mutationKey: [...TEMPLATES_KEY_ROOT, "create"],
    mutationFn: (body) => api.post<AssessmentTemplatePublic>("/assessments/templates", body),
    onSuccess: () => invalidateTemplates(qc),
  });
}

export interface UseUpdateAssessmentTemplateVars {
  slug: string;
  body: AssessmentTemplateUpdate;
}

export function useUpdateAssessmentTemplate() {
  const qc = useQueryClient();
  return useMutation<AssessmentTemplatePublic, Error, UseUpdateAssessmentTemplateVars>({
    mutationKey: [...TEMPLATES_KEY_ROOT, "update"],
    mutationFn: ({ slug, body }) =>
      api.patch<AssessmentTemplatePublic>(`/assessments/templates/${slug}`, body),
    onSuccess: () => invalidateTemplates(qc),
  });
}

export interface UseActivateAssessmentTemplateVars {
  slug: string;
  note?: string;
}

export function useActivateAssessmentTemplate() {
  const qc = useQueryClient();
  return useMutation<AssessmentTemplatePublic, Error, UseActivateAssessmentTemplateVars>({
    mutationKey: [...TEMPLATES_KEY_ROOT, "activate"],
    mutationFn: ({ slug, note }) =>
      api.post<AssessmentTemplatePublic>(`/assessments/templates/${slug}:activate`, {
        note: note ?? null,
      }),
    onSuccess: () => invalidateTemplates(qc),
  });
}

export interface UseArchiveAssessmentTemplateVars {
  slug: string;
  reason: string;
}

export function useArchiveAssessmentTemplate() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, Error, UseArchiveAssessmentTemplateVars>({
    mutationKey: [...TEMPLATES_KEY_ROOT, "archive"],
    mutationFn: ({ slug, reason }) =>
      api.delete<{ ok: boolean }>(`/assessments/templates/${slug}`, {
        data: { reason },
      }),
    onSuccess: () => invalidateTemplates(qc),
  });
}
